import { VERIFIED_ROLES, type Role } from './roles';

/**
 * Kinds of account. Each matches a login door in the app (patient, doctor,
 * pharmacy, clinic staff), plus the platform admin, who signs in on the admin
 * pages, and the hub, a clinic PC that signs in with its device key. One
 * person may hold separate accounts of different types on the same mobile
 * number, for example a doctor who is also a patient.
 *
 * Practice roles come from practice memberships, not from the account. A
 * staff account becomes practice_admin or front_desk only through a
 * membership, and a doctor account may also be practice_admin of its practice.
 * A hub holds no role: it acts only as its practice's record holder.
 */
export const ACCOUNT_TYPES = ['patient', 'doctor', 'pharmacist', 'staff', 'platform_admin', 'hub'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

/** Account types that belong to people and sign in with a mobile number and password. */
export const PERSON_ACCOUNT_TYPES = ['patient', 'doctor', 'pharmacist', 'staff', 'platform_admin'] as const;
export type PersonAccountType = (typeof PERSON_ACCOUNT_TYPES)[number];

export function isAccountType(value: unknown): value is AccountType {
  return typeof value === 'string' && (ACCOUNT_TYPES as readonly string[]).includes(value);
}

/** Account types people can sign up for in the app. Platform admins come from a server script. */
export const SIGNUP_ACCOUNT_TYPES = ['patient', 'doctor', 'pharmacist', 'staff'] as const;
export type SignupAccountType = (typeof SIGNUP_ACCOUNT_TYPES)[number];

/** Roles each account type holds in its own right, before any practice membership. */
export const ACCOUNT_TYPE_ROLES: Readonly<Record<AccountType, readonly Role[]>> = {
  patient: ['patient'],
  doctor: ['doctor'],
  pharmacist: ['pharmacist'],
  staff: [],
  platform_admin: ['platform_admin'],
  hub: [],
};

/**
 * pending: waiting for the platform's verification staff (doctors and pharmacists).
 * active: may use the service.
 * rejected: verification failed. The account sees the reason and nothing else.
 * suspended: blocked by the platform admin. The account sees the reason and nothing else.
 */
export const ACCOUNT_STATUSES = ['pending', 'active', 'rejected', 'suspended'] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/** True when this account type carries a professional registration that staff must verify. */
export function requiresVerification(type: AccountType): boolean {
  return ACCOUNT_TYPE_ROLES[type].some((role) => VERIFIED_ROLES.includes(role));
}

/** The status a new account starts in. */
export function initialStatus(type: AccountType): AccountStatus {
  return requiresVerification(type) ? 'pending' : 'active';
}

/** A doctor's medical council registration or a pharmacist's pharmacy council registration. */
export interface ProfessionalRegistration {
  council: string;
  number: string;
}

/** An account as the server returns it. Login secrets never leave the server. */
export interface PublicAccount {
  id: string;
  type: AccountType;
  roles: Role[];
  status: AccountStatus;
  statusReason?: string;
  name: string;
  mobile: string;
  email?: string;
  abhaNumber?: string;
  abhaAddress?: string;
  registration?: ProfessionalRegistration;
  /** A doctor's or pharmacist's qualifications as printed on prescriptions, such as "MBBS, MD (Medicine)". */
  qualifications?: string;
  createdAt: string;
}

/** Longest qualifications text an account may set. */
export const MAX_QUALIFICATIONS_LENGTH = 100;
